import { useEffect, useRef, useState } from 'react';
import { Alert, Button, Select } from '@omnitok/ui';
import JsonViewer from '@/features/localization/components/JsonViewer';
import { LoadingSpinner } from '@/shared/components';
import { consolidationService } from './service';
import { getRowError } from './utils';
import type { Configuration, IndexadoPreview, ResultRow, RowError } from './types';

function displayField(document: Record<string, unknown>, path: string): string {
  let value: unknown = document;
  for (const key of path.split('.')) {
    if (!value || typeof value !== 'object' || !Object.prototype.hasOwnProperty.call(value, key)) return '(no existe)';
    value = (value as Record<string, unknown>)[key];
  }
  if (path === 'lang' && value && typeof value === 'object') return `Localizaciones: ${Object.keys(value).join(', ') || 'ninguna'}`;
  return typeof value === 'string' ? value : JSON.stringify(value) ?? '(no existe)';
}

export default function IndexadoPreviewPanel({ row, configuration, view, onLoaded }: {
  row: ResultRow; configuration: Configuration; view: 'changes' | 'preview';
  onLoaded: (indexados: IndexadoPreview[]) => void;
}) {
  const [error, setError] = useState<RowError | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [documentId, setDocumentId] = useState('');
  const onLoadedRef = useRef(onLoaded);
  onLoadedRef.current = onLoaded;
  useEffect(() => {
    if (row.indexados || !row.selection || row.status === 'integrated') return;
    let active = true;
    setError(null);
    void consolidationService.previewIndexados(configuration, row.selection).then(indexados => {
      if (active) onLoadedRef.current(indexados);
    }).catch(cause => { if (active) setError(getRowError(cause)); });
    return () => { active = false; };
  }, [configuration, row.indexados, row.selection, row.status, attempt]);

  if (!row.indexados) {
    if (row.status === 'integrated') return <Alert variant="info">No se consultó el preview de las fichas antes de aplicar esta fila. En “Resultado” puedes ver los IDs de los Indexados migrados.</Alert>;
    if (error) return <div className="localization-dialog-content">
      <Alert variant="error">{error.message}</Alert><p className="localization-muted">Código: {error.code}</p>
      <div><Button variant="outline" onClick={() => setAttempt(current => current + 1)}>Reintentar consulta de Indexados</Button></div>
    </div>;
    return <LoadingSpinner message="Consultando los Indexados y sus cambios propuestos…" />;
  }
  const documents = row.indexados;
  const document = documents.find(item => item.id === documentId) ?? documents[0];
  if (!document) return <Alert variant="info">No hay Indexados para mostrar.</Alert>;
  return <div className="localization-dialog-content">
    <Alert variant="info">{documents.filter(item => item.action === 'migrate').length} ficha(s) se reasignarán al modelo base
      {documents.some(item => item.action === 'overwrite') && `; ${documents.filter(item => item.action === 'overwrite').length} ficha(s) del destino perderán la localización reemplazada`}.
      {' '}Esta consulta no guarda cambios ni crea backups.</Alert>
    <Select label="Indexado a revisar" value={document.id} fullWidth onChange={event => setDocumentId(event.target.value)}
      options={documents.map(item => ({ value: item.id,
        label: `${item.id} · ${item.action === 'migrate' ? 'Reasignar al modelo base' : 'Limpiar localización del destino'}${item.before.state === false ? ' · Inactivo' : ''}` }))} />
    <dl className="consolidation-change-list">
      <div><dt>ID del Indexado · se conserva</dt><dd>{document.id}</dd></div>
      <div><dt>Operación</dt><dd>{document.action === 'migrate' ? 'Migrar ficha y su localización' : 'Retirar la localización existente por sobrescritura'}</dd></div>
    </dl>
    {view === 'changes' ? <>
      <div className="consolidation-table-scroll"><table className="consolidation-table">
        <thead><tr><th scope="col">Campo</th><th scope="col">Antes</th><th scope="col">Después</th></tr></thead>
        <tbody>{document.changedFields.map(path => <tr key={path}><th scope="row">{path}</th>
          <td>{displayField(document.before, path)}</td><td>{displayField(document.after, path)}</td></tr>)}</tbody>
      </table></div>
      <div className="consolidation-preview-columns">
        <section><h3>Indexado original</h3><JsonViewer value={document.before} fileName={`${document.id}.original.json`} /></section>
        <section><h3>Indexado propuesto</h3><JsonViewer value={document.after} fileName={`${document.id}.preview.json`} /></section>
      </div>
    </> : <>
      <h3>Indexado después de consolidar</h3>
      <JsonViewer value={document.after} fileName={`${document.id}.preview.json`} />
      <details><summary>Ver Indexado original</summary><JsonViewer value={document.before} fileName={`${document.id}.original.json`} /></details>
    </>}
    <p className="localization-muted">El plan usa los datos guardados al consultar. Al aplicar se vuelven a validar los documentos; la fecha modified se establece en ese momento.</p>
  </div>;
}
